import { ReactNode } from 'react'
import cx from 'classnames'
import { StatusChip, StatusChipVariant } from '~m/designSystem'
import styles from './ConfidenceBridge.module.css'

/**
 * What a reconciliation row will do with the incoming record.
 *
 * - `link` — attach it to an existing Soniq contact.
 * - `create` — add it as a new Soniq contact.
 * - `skip` — leave it out of the sync.
 */
export type SyncAction = 'link' | 'create' | 'skip'

/**
 * Confidence tier of a proposed match.
 *
 * - `strong` / `possible` — a fuzzy-match candidate, coloured by
 *   how confident the matcher is.
 * - `none` — no candidate was found.
 * - `manual` — the user picked a target by hand, so there is no score.
 * - `new` — the record will be created rather than matched.
 */
export type ConfidenceTier =
  | 'strong'
  | 'possible'
  | 'none'
  | 'manual'
  | 'new'

/** Maps each tier onto a StatusChip colour so badges stay on-palette. */
const TIER_VARIANT: Record<ConfidenceTier, StatusChipVariant> = {
  strong: 'success',
  possible: 'warning',
  none: 'neutral',
  manual: 'info',
  new: 'info',
}

export interface ConfidenceBridgeProps {
  /** The confidence tier, driving the badge colour */
  tier: ConfidenceTier
  /** The short tier label, e.g. "Strong" or "New" */
  label: string
  /**
   * Match score as a percentage. When set it is prefixed to the
   * label. Only meaningful for scored tiers (`strong` / `possible`).
   */
  percent?: number
  /**
   * What the row will do. Drives the marker: the arrow icon turns
   * to point down on narrow screens when linking, and a skipped
   * row fades the whole bridge.
   */
  action: SyncAction
  /**
   * Icon inside the round marker, e.g. an arrow for link, a plus
   * for create, a crossed circle for skip. Supply it from the
   * app's icon set.
   */
  icon?: ReactNode
  /** Additional CSS class */
  className?: string
}

/**
 * The centre column of a reconciliation row. Shows how sure the
 * match is (a StatusChip badge) over a small round marker that
 * says what will happen to the record.
 */
export function ConfidenceBridge({
  tier,
  label,
  percent,
  action,
  icon,
  className,
}: ConfidenceBridgeProps) {
  const badgeLabel =
    percent != null ? `${percent}% ${label}` : label

  return (
    <div
      className={cx(styles.ConfidenceBridge, className, {
        [styles.isLink]: action === 'link',
        [styles.isSkipped]: action === 'skip',
      })}
    >
      <StatusChip
        size="small"
        variant={TIER_VARIANT[tier]}
        label={badgeLabel}
      />
      {icon && (
        <span
          className={styles.ConfidenceBridge__marker}
          aria-hidden
        >
          {icon}
        </span>
      )}
    </div>
  )
}
